import React from 'react';
import { Select } from 'antd';
// import { CalendarOutlined, ExclamationCircleOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styles from './StatusSelector.module.scss';
import { getStatusColor } from '../../utils/statusHelpers';
import {
  canChangeEntryStatus,
  canChangeEntryStatusOnPage,
} from '../../constants/roles';

const { Option } = Select;

interface StatusSelectorProps {
  className?: string;
  /** Select açılır listesi ek sınıfı (örn. player üst şeritte 10px metin) */
  selectPopupExtraClassName?: string;
  /** false: liste genişliği tetikleyiciye kilitlenmez, içeriğe göre daralır */
  popupMatchSelectWidth?: boolean;
  onChange: (status: string) => void;
  disabled: boolean;
  userRole?: string;
  selectedItems?: Set<number>;
  selectedItemsDetails?: Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any }>;
  fileDetail?: any;
  currentEntry?: any;
  selectedVersion?: number;
  canMultiSelect: () => boolean;
}

const StatusSelector: React.FC<StatusSelectorProps> = ({
  className,
  selectPopupExtraClassName,
  popupMatchSelectWidth,
  onChange,
  disabled,
  userRole,
  selectedItems,
  selectedItemsDetails,
  fileDetail,
  currentEntry,
  selectedVersion,
  canMultiSelect
}) => {
  // Get current status
  const getCurrentStatus = () => {
    if (selectedItems && selectedItems.size > 0) {
      // Get status from first selected item
      const firstSelectedItem = selectedItemsDetails?.[0];
      return firstSelectedItem?.status || 'NeedsReview';
    } else if (fileDetail) {
      // Get status from selected version or file
      if (selectedVersion && fileDetail.versions) {
        const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData) {
          return selectedVersionData.status || fileDetail.status || 'NeedsReview';
        }
      }
      return fileDetail.status || 'NeedsReview';
    } else if (currentEntry) {
      return currentEntry.status || 'NeedsReview';
    }
    return 'NeedsReview';
  };

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'NeedsReview':
        return 'Needs Review';
      case 'Updated':
        return 'Updated';
      case 'Approved':
        return 'Approved';
      default:
        return status;
    }
  };

  const renderStatusOption = (status: string, label: string) => (
    <span className={styles.statusOption}>
      <span className={styles.statusDot} style={{ backgroundColor: getStatusColor(status) }} />
      <span className={styles.statusLabel}>{label}</span>
    </span>
  );

  // POST /api/Entry/change-status: Admin, SuperAdmin, Editor, Commenter
  const getStatusOptions = () => {
    if (!userRole || !canChangeEntryStatus(userRole)) {
      return [] as Array<{ value: string; label: string; disabled: boolean }>;
    }
    const all = ['NeedsReview', 'Updated', 'Approved'];
    return all.map((value) => ({
      value,
      label: getStatusLabel(value),
      disabled: false
    }));
  };

  const canChangeStatus = () =>
    canChangeEntryStatusOnPage(userRole, { hasFileDetail: !!fileDetail });

  const currentStatus = getCurrentStatus();
  const isDisabled = disabled || 
    !canChangeStatus() || 
    (selectedItems && selectedItems.size === 0) || 
    (selectedItems && selectedItems.size > 1 && !canMultiSelect());

  // Should show current status value (only in FileDetail page with selected version)
  const shouldShowValue = () => {
    return !!(fileDetail && selectedVersion);
  };

  const rootClass = [styles.statusContainer, className].filter(Boolean).join(' ');

  const readOnlyStatusDisplay = (
    <div className={rootClass}>
      <div className={styles.statusDisplay}>
        <div className={styles.statusDisplayContent}>
          <span className={styles.statusLabel}>{getStatusLabel(currentStatus)}</span>
          <div
            className={styles.statusIndicator}
            style={{ backgroundColor: getStatusColor(currentStatus) }}
          />
        </div>
      </div>
    </div>
  );

  // Guest, Viewer ve change-status dışı roller — salt okunur
  if (!canChangeEntryStatusOnPage(userRole, { hasFileDetail: !!fileDetail })) {
    return readOnlyStatusDisplay;
  }

  return (
    <div className={rootClass}>
      <Select
        placeholder="Status"
        value={shouldShowValue() ? getCurrentStatus() : undefined}
        onChange={onChange}
        className={styles.statusSelect}
        popupClassName={[styles.statusDropdown, selectPopupExtraClassName].filter(Boolean).join(' ')}
        popupMatchSelectWidth={popupMatchSelectWidth}
        disabled={isDisabled}
      >
        {getStatusOptions().map((option) => (
          <Option key={option.value} value={option.value} disabled={option.disabled}>
            {renderStatusOption(option.value, option.label)}
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default StatusSelector; 